<script setup lang="ts">
import type { ThemeSettings, SpotifyEmbedSettings } from '~/stores/auth'
import { DEFAULT_SPOTIFY_EMBED_SETTINGS } from '~/stores/auth'
import { hexToRgb } from '~/composables/useLayoutStyles'
import { useCardHover } from '~/composables/useCardHover'

interface SpotifyTrackData {
  id: string
  name: string
  artistName: string
  albumName: string
  albumArtUrl: string | null
  spotifyUrl: string
  durationMs: number
  progressMs?: number
}

interface SpotifyPlayerData {
  nowPlaying: {
    isPlaying: boolean
    track: SpotifyTrackData
  } | null
  recentlyPlayed: {
    track: SpotifyTrackData
    playedAt: string
  } | null
  topTracks: SpotifyTrackData[]
  profile: {
    displayName: string | null
    avatarUrl: string | null
    spotifyUrl: string
  }
  error?: string
}

const props = defineProps<{
  characterId: string
  themeSettings: ThemeSettings
  spotifySettings?: SpotifyEmbedSettings
  cardStyle?: Record<string, string>
  editMode?: boolean
}>()

const emit = defineEmits<{
  'open-spotify-settings': []
}>()

const skinPreview = inject<boolean>('skinPreview', false)
const { onCardMouseMove, onCardMouseLeave } = useCardHover(toRef(props, 'themeSettings'))

const sp = computed<SpotifyEmbedSettings>(() => ({
  ...DEFAULT_SPOTIFY_EMBED_SETTINGS,
  ...(props.spotifySettings ?? {})
}))

const playerData = ref<SpotifyPlayerData | null>(null)
const loaded = ref(false)
const tokenExpired = ref(false)

// Client-side progress interpolation
const localProgressMs = ref(0)
const lastSyncTime = ref(0)
let tickInterval: ReturnType<typeof setInterval> | null = null
let pollInterval: ReturnType<typeof setInterval> | null = null
let endOfTrackTimeout: ReturnType<typeof setTimeout> | null = null

async function fetchPlayerData() {
  const params: Record<string, string> = {
    topTracksTimeRange: sp.value.topTracksTimeRange,
    topTracksCount: String(sp.value.topTracksCount)
  }
  const data = await $fetch<SpotifyPlayerData>(`/api/spotify/player/${props.characterId}`, { params })

  if (data.error === 'token_expired') {
    tokenExpired.value = true
    return
  }

  tokenExpired.value = false
  playerData.value = data
  syncProgress()
}

function syncProgress() {
  const np = playerData.value?.nowPlaying
  if (np?.track?.progressMs != null && np.isPlaying) {
    localProgressMs.value = np.track.progressMs
    lastSyncTime.value = Date.now()
    scheduleEndOfTrackFetch()
  } else {
    localProgressMs.value = np?.track?.progressMs ?? 0
    lastSyncTime.value = 0
    clearEndOfTrackTimeout()
  }
}

function scheduleEndOfTrackFetch() {
  clearEndOfTrackTimeout()
  const np = playerData.value?.nowPlaying
  if (!np?.isPlaying || !np.track?.durationMs || !np.track?.progressMs) return

  const remainingMs = np.track.durationMs - np.track.progressMs
  // Fetch 1.5s after the track should end (give Spotify time to update)
  const delay = remainingMs + 1500
  if (delay > 0 && delay < 600_000) {
    endOfTrackTimeout = setTimeout(async () => {
      try { await fetchPlayerData() } catch { /* silent */ }
    }, delay)
  }
}

function clearEndOfTrackTimeout() {
  if (endOfTrackTimeout) {
    clearTimeout(endOfTrackTimeout)
    endOfTrackTimeout = null
  }
}

onMounted(async () => {
  if (skinPreview) {
    loaded.value = true
    return
  }
  try {
    await fetchPlayerData()
  } catch {
    // Silent fail - component won't render
  } finally {
    loaded.value = true
  }

  // Tick every second to interpolate progress bar
  tickInterval = setInterval(() => {
    if (lastSyncTime.value > 0 && playerData.value?.nowPlaying?.isPlaying) {
      const elapsed = Date.now() - lastSyncTime.value
      const np = playerData.value.nowPlaying.track
      if (np) {
        localProgressMs.value = Math.min(
          (np.progressMs ?? 0) + elapsed,
          np.durationMs
        )
      }
    }
  }, 1000)

  // Periodic sync every 30 seconds as a safety net
  if (sp.value.showNowPlaying) {
    pollInterval = setInterval(async () => {
      try { await fetchPlayerData() } catch { /* silent */ }
    }, 30_000)
  }
})

onUnmounted(() => {
  if (tickInterval) { clearInterval(tickInterval); tickInterval = null }
  if (pollInterval) { clearInterval(pollInterval); pollInterval = null }
  clearEndOfTrackTimeout()
})

// Re-fetch when settings change (e.g. after saving embed settings)
watch(() => props.spotifySettings, async () => {
  if (!loaded.value) return
  try {
    await fetchPlayerData()
  } catch {
    // Silent fail
  }
}, { deep: true })

const cardBg = computed(() => {
  const rgb = hexToRgb(props.themeSettings.cardColor)
  return `rgba(${rgb.join(',')}, ${Math.min(props.themeSettings.cardOpacity / 100, 0.6)})`
})

// Current track to display (now playing or recently played fallback)
const displayTrack = computed(() => {
  if (playerData.value?.nowPlaying?.track) {
    return playerData.value.nowPlaying.track
  }
  if (playerData.value?.recentlyPlayed?.track) {
    return playerData.value.recentlyPlayed.track
  }
  return null
})

const isPlaying = computed(() => playerData.value?.nowPlaying?.isPlaying ?? false)

const playbackLabel = computed(() => {
  if (playerData.value?.nowPlaying) {
    return playerData.value.nowPlaying.isPlaying ? 'Now Playing' : 'Paused'
  }
  if (playerData.value?.recentlyPlayed) {
    return 'Recently Played'
  }
  return null
})

// Progress bar percentage (uses client-interpolated value)
const progressPercent = computed(() => {
  const track = playerData.value?.nowPlaying?.track
  if (!track?.durationMs) return 0
  return Math.min((localProgressMs.value / track.durationMs) * 100, 100)
})

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}
</script>

<template>
  <SkinPreviewPlaceholder
    v-if="skinPreview"
    label="Spotify Embed"
    icon="i-simple-icons-spotify"
    :card-style="cardStyle"
    :theme-settings="themeSettings"
  />
  <div
    v-else-if="loaded && playerData && !tokenExpired"
    class="group rounded-xl overflow-hidden"
    :style="{
      background: cardBg,
      ...cardStyle
    }"
    @mousemove="onCardMouseMove"
    @mouseleave="onCardMouseLeave"
  >
    <!-- Profile header -->
    <a
      :href="playerData.profile.spotifyUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="flex items-center gap-2.5 px-3 pt-3 pb-2 no-underline group/header"
    >
      <img
        v-if="playerData.profile.avatarUrl"
        :src="playerData.profile.avatarUrl"
        :alt="playerData.profile.displayName ?? 'Spotify'"
        class="size-10 rounded-full shrink-0 object-cover"
        referrerpolicy="no-referrer"
      >
      <div
        v-else
        class="size-10 rounded-full bg-[#1DB954]/20 flex items-center justify-center shrink-0"
      >
        <UIcon
          name="i-simple-icons-spotify"
          class="size-4 text-[#1DB954]"
        />
      </div>
      <div class="min-w-0 flex-1">
        <span class="text-sm font-semibold text-white leading-tight block truncate group-hover/header:text-[#1DB954] transition-colors">
          {{ playerData.profile.displayName ?? 'Spotify' }}
        </span>
        <span class="text-xs text-white/40 leading-tight block">
          Listening on Spotify
        </span>
      </div>
    </a>

    <!-- Now Playing / Recently Played -->
    <div
      v-if="sp.showNowPlaying && displayTrack"
      class="px-3 pb-2"
    >
      <!-- Status label -->
      <div class="flex items-center gap-1.5 mb-2">
        <span
          v-if="isPlaying"
          class="flex items-center gap-1"
        >
          <span class="flex gap-[2px] items-end h-3">
            <span
              class="w-[3px] bg-[#1DB954] rounded-full animate-[equalizer_0.8s_ease-in-out_infinite]"
              style="height: 8px;"
            />
            <span
              class="w-[3px] bg-[#1DB954] rounded-full animate-[equalizer_0.8s_ease-in-out_0.2s_infinite]"
              style="height: 12px;"
            />
            <span
              class="w-[3px] bg-[#1DB954] rounded-full animate-[equalizer_0.8s_ease-in-out_0.4s_infinite]"
              style="height: 6px;"
            />
          </span>
        </span>
        <span
          class="text-[10px] uppercase tracking-wider font-medium"
          :class="isPlaying ? 'text-[#1DB954]' : 'text-white/30'"
        >
          {{ playbackLabel }}
        </span>
        <span
          v-if="playerData.recentlyPlayed && !playerData.nowPlaying"
          class="text-[10px] text-white/20"
        >
          {{ timeAgo(playerData.recentlyPlayed.playedAt) }}
        </span>
      </div>

      <!-- Track info -->
      <a
        :href="displayTrack.spotifyUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center gap-3 no-underline group/track"
      >
        <img
          v-if="displayTrack.albumArtUrl"
          :src="displayTrack.albumArtUrl"
          :alt="displayTrack.albumName"
          class="size-14 rounded-lg shrink-0 object-cover"
          loading="lazy"
          referrerpolicy="no-referrer"
        >
        <div
          v-else
          class="size-14 rounded-lg bg-white/5 flex items-center justify-center shrink-0"
        >
          <UIcon
            name="i-lucide-music"
            class="size-6 text-white/20"
          />
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-medium text-white leading-tight truncate group-hover/track:text-[#1DB954] transition-colors">
            {{ displayTrack.name }}
          </p>
          <p class="text-xs text-white/50 leading-tight truncate mt-0.5">
            {{ displayTrack.artistName }}
          </p>
          <p class="text-[11px] text-white/30 leading-tight truncate mt-0.5">
            {{ displayTrack.albumName }}
          </p>
        </div>
      </a>

      <!-- Progress bar (only when actively playing) -->
      <div
        v-if="playerData.nowPlaying?.track?.progressMs && playerData.nowPlaying?.track?.durationMs"
        class="mt-2 flex items-center gap-2"
      >
        <span class="text-[10px] text-white/30 tabular-nums">{{ formatDuration(localProgressMs) }}</span>
        <div class="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            class="h-full bg-[#1DB954] rounded-full transition-all duration-500"
            :style="{ width: `${progressPercent}%` }"
          />
        </div>
        <span class="text-[10px] text-white/30 tabular-nums">{{ formatDuration(playerData.nowPlaying.track.durationMs) }}</span>
      </div>
    </div>

    <!-- Top Tracks -->
    <div
      v-if="sp.showTopTracks && playerData.topTracks.length > 0"
      class="px-3 pb-2"
    >
      <div
        v-if="sp.showNowPlaying && displayTrack"
        class="border-t border-white/6 my-2"
      />
      <p class="text-[10px] uppercase tracking-wider font-medium text-white/30 mb-1.5">
        Top Tracks
      </p>
      <div class="space-y-1.5">
        <a
          v-for="(track, index) in playerData.topTracks"
          :key="track.id"
          :href="track.spotifyUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-2.5 no-underline group/track"
        >
          <span class="text-[11px] text-white/20 w-4 text-right tabular-nums shrink-0">{{ index + 1 }}</span>
          <img
            v-if="track.albumArtUrl"
            :src="track.albumArtUrl"
            :alt="track.albumName"
            class="size-8 rounded shrink-0 object-cover"
            loading="lazy"
            referrerpolicy="no-referrer"
          >
          <div
            v-else
            class="size-8 rounded bg-white/5 flex items-center justify-center shrink-0"
          >
            <UIcon
              name="i-lucide-music"
              class="size-3 text-white/20"
            />
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-xs text-white/70 leading-tight truncate group-hover/track:text-[#1DB954] transition-colors">
              {{ track.name }}
            </p>
            <p class="text-[11px] text-white/30 leading-tight truncate">
              {{ track.artistName }}
            </p>
          </div>
        </a>
      </div>
    </div>

    <!-- Footer with Spotify branding (required by ToS) -->
    <div class="flex items-center justify-end px-3 pb-2.5">
      <UIcon
        name="i-simple-icons-spotify"
        class="size-3 text-white/15 group-hover:text-[#1DB954] transition-colors"
      />
    </div>

    <!-- Manage button (edit mode) -->
    <button
      v-if="editMode"
      class="w-full flex items-center justify-center gap-2 py-2 px-4 border-t border-white/6 transition-all duration-200 hover:bg-white/4 cursor-pointer"
      :style="{ color: 'rgba(255, 255, 255, 0.4)' }"
      @click="emit('open-spotify-settings')"
    >
      <UIcon
        name="i-lucide-settings"
        class="size-4"
      />
      <span class="text-sm">Manage embed</span>
    </button>
  </div>
</template>

<style scoped>
@keyframes equalizer {
  0%, 100% { height: 4px; }
  50% { height: 12px; }
}
</style>
